import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import App from "../App"
import CategoryMenu from "../components/CategoryMenu";
import { getNewestListings } from "../services/ListingService";
import ListingCard from "../components/ListingCard"
import Recommendations from "../components/Recommendations";
import SeoHead from "../components/SeoHead";
import { useNavigate } from 'react-router-dom';
import SearchBar from "../components/SearchBar";
import LoadingComponent from "../components/LoadingComponent"

/**
 * Home page component
 * Displays newest listings and search functionality
 */
function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchString, setSearchString] = useState("");
  const [searchCategoryId, setSearchCategoryId] = useState(null);
  const [listings, setListings] = useState(null);
  const [isListLoading, setIsListLoading] = useState(false);

  useEffect(() => {
    if ((searchCategoryId !== null && (Array.isArray(searchCategoryId) && searchCategoryId.length > 0))) {
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("sort", "date_from,desc");
      if (searchCategoryId != null) params.set("categories", String(searchCategoryId));
      navigate(`/search?${params.toString()}`);
    }
  }, [searchCategoryId, navigate]);


  // Fetch newest listings
  useEffect(() => {
    const loadNewestListings = async () => {
      setIsListLoading(true);
      try {
        const { data, meta } = await getNewestListings();
        if (meta) {
          console.warn('Newest listings warning:', meta);
        }
        setListings(data);
      } catch (err) {
        console.error('Error fetching newest listings:', err);
        setListings(null);
      } finally {
        setIsListLoading(false);
      }
    };

    loadNewestListings();
  }, []);

  /** Handle category selection (receives category ID or null). */
  const handleCategorySelect = useCallback((categoryId, categoryIds) => {
    setSearchCategoryId(categoryIds ?? null);
  }, []);

  /**
   * Handle search button click
   */
  const handleSearch = useCallback(() => {
   /* // Cancel any pending autocomplete requests first
    if (searchAutocompleteRef.current && searchAutocompleteRef.current.cancelRequests) {
      searchAutocompleteRef.current.cancelRequests();
    }*/

    if (searchString && searchString.length > 2) {
      //pendingNavigationRef.current = true;
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("sort", "date_from,desc");
      params.set("q", searchString);
      navigate(`/search?${params.toString()}`);

    }
  }, [searchString, navigate]);

  
  return (
    <App>
      <SeoHead />
      <Container maxWidth="xl">
        <Stack spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ py: { xs: 2, sm: 3 } }}>
          {/* Category Menu */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
              px: { xs: 1, sm: 2 },
            }}
          >
            <CategoryMenu onCategorySelect={handleCategorySelect} />
          </Box>

          {/* Search Section */}
          <SearchBar
            value={searchString}
            onChange={(event, newValue) => {
              setSearchString(newValue || '');
            }}
            onEnter={() => handleSearch()}
            onSelect={(suggestion) => {
              setSearchString(suggestion.title);
              //pendingNavigationRef.current = true;
            }}
            onSearch={() => handleSearch()}
            isMobile={isMobile}
            //searchAutocompleteRef={searchAutocompleteRef}
            resolveCategoriesParam={(suggestion) => {
              if (suggestion?.categoryId) {
                return [suggestion.categoryId];
              }
              return [];
            }}
          />

          {/* Trending Listings */}
          <Box sx={{ px: { xs: 1, sm: 2 } }}>
            <Recommendations 
              limit={16} 
              title="Popularne ogłoszenia" 
              type="trending"
            />
          </Box>

          {/* Newest Listings Section */}
          <Box sx={{ px: { xs: 1, sm: 2 } }}>
            <Typography 
              variant="h5" 
              component="h2" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontWeight: 600,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }
              }}
            >
              Najnowsze ogłoszenia
            </Typography>
            
            {isListLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <LoadingComponent />
              </Box>
            ) : !listings?.content?.length ? (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: { xs: 4, sm: 6 },
                  px: 2
                }}
              >
                <Typography 
                  variant="h6" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                >
                  Brak ogłoszeń do wyświetlenia
                </Typography>
              </Box>
            ) : (
              <Grid 
                container 
                spacing={{ xs: 2, sm: 3, md: 4 }}
                sx={{ 
                  mt: { xs: 1, sm: 2 },
                  justifyContent: { xs: "center", sm: "flex-start" }
                }}
              >
                {listings.content.map((listing) => (
                  <ListingCard key={listing.id} listing={listing} />
                ))}
              </Grid>
            )}
          </Box>
        </Stack>
      </Container>
    </App>
  );
}

export default Home;
